/**
 * The state for a single campaign.
 */
import axios from 'axios';
import { Deliverable } from '@/classes/Deliverable';
import { DeliverableDraft } from '@/classes/DeliverableDraft';

/**
 * @typedef {Object} Campaign
 * @type {Object}
 * @property {String} assetsLink
 * @property {String} brandName
 * @property {String} brandPortalHandle
 * @property {Number} brandPortalId
 * @property {String} contactEmail
 * @property {String} contactName
 * @property {Boolean} contentCollaborationFFEnabled
 * @property {Boolean} deliverablesEnabled
 * @property {String} description
 * @property {String} handle
 * @property {Boolean} includeOauthLink
 * @property {String} name
 * @property {Number} projectId
 * @property {String} projectInfStatus
 * @property {String} publishDate
 * @property {String} publishStatus
 * @property {String} reviewDate
 * @property {String} summary
 */
/**
 * @typedef {import('../../../components/campaign/CampaignDraftForm.vue').DraftFormData} DraftFormData
 */

const state = () => ({
    brandPortal: null,
    infUid: null,
    // The campaign all loaded state pertains to.
    campaign: null,
    // Influencer's deliverables for campaign.
    deliverables: [],
    deliverableDrafts: [],
    draftFileSizeLimits: null,
    tiktokOrders: [],
    ttoCampaign: null
});

// mutations
const mutations = {
    setBrandPortal(state, brandPortal) {
        state.brandPortal = { ...brandPortal };
    },
    setInfUid(state, infUid) {
        state.infUid = infUid;
    },
    setCampaign(state, campaign) {
        state.campaign = { ...campaign };
    },
    setDeliverables(state, deliverables) {
        state.deliverables = [...deliverables];
    },
    setDeliverableDrafts(state, deliverableDrafts) {
        state.deliverableDrafts = [...deliverableDrafts];
    },
    setDraftFileSizeLimits(state, limits) {
        state.draftFileSizeLimits = { ...limits };
    },
    setTiktokOrders(state, orders) {
        state.tiktokOrders = [...orders];
    },
    setTtoCampaign(state, campaign) {
        state.ttoCampaign = { ...campaign };
    }
};

// getters
const getters = {
    brandPortal(state) {
        return state.brandPortal;
    },
    /**
     * Get the stored campaign
     * @param {*} state
     * @returns {Campaign}
     */
    campaign(state) {
        return { ...state.campaign };
    },
    infUid(state) {
        return state.infUid;
    },
    /**
     * Get the stored deliverables
     * @param {*} state
     * @returns {Array.<Deliverable>}
     */
    deliverables(state)  {
        return [...state.deliverables];
    },
    /**
     * Get the stored drafts
     * @param {*} state
     * @returns {Array.<DeliverableDraft>}
     */
    deliverableDrafts(state) {
        return [...state.deliverableDrafts];
    },
    draftFileSizeLimits(state)  {
        return { ...state.draftFileSizeLimits };
    },
    /**
     * Get the drafts related to the given deliverable
     * @param {*} state
     * @returns {Array.<DeliverableDraft>}
     */
    getDraftsByDeliverable(state) {
        return function (deliverable) {
            return state.deliverableDrafts.filter((draft) => draft.deliverableId === deliverable.id);
        };
    },
    /**
     * Get the tiktok orders
     * @param {*} state
     * @returns {Array.<Object>}
     */
    tiktokOrders(state) {
        return [...state.tiktokOrders];
    },
    /**
     * Get the tiktok orders related to the given deliverable
     * @param {*} state
     * @returns {Array.<Object>}
     * @param {Deliverable} deliverable
     * @returns {Array.<TiktokOrders>}
     */
    getTiktokOrderByDeliverable(state) {
        return function (deliverable) {
            return state.tiktokOrders.find((order) => String(order.deliverableId) === String(deliverable.id));
        };
    },
    /**
     * Get the tto campaign
     * @param {*} state
     * @returns {Object}
     */
    getTtoCampaign(state) {
        return { ...state.ttoCampaign };
    }
};

// actions
const actions = {
    loadBrandPortal({ commit }, brandPortal) {
        commit('setBrandPortal', brandPortal);
    },
    /**
     * Load a campaign's data
     * @param {Object} param0
     * @param {Campaign} campaign
     */
    loadCampaign({ commit }, campaign) {
        commit('setCampaign', campaign);
    },
    loadInfUid({ commit }, infUid) {
        commit('setInfUid', infUid);
    },
    loadDraftFileSizeLimits({ commit }, limits) {
        commit('setDraftFileSizeLimits', limits);
    },
    /**
     * Load deliverables for campaign into the `deliverables` state property.
     * @param {Object} options:
     *  {Bool} forceUpdate Default false. True to force fetch from backend;
     *                     False to allow existing state value to be used.
     * @throws {Error}
     */
    async loadDeliverables({ commit, state }) {
        if (!state.campaign) {
            throw new Error('campaign/loadDeliverables: campaign not loaded.');
        }
        const url = `/campaigns/${state.campaign.handle}/deliverables`;
        const { data } = await axios.get(url);
        const deliverables = data.deliverables.map((deliverable) => new Deliverable(deliverable));
        commit('setDeliverables', deliverables);
    },
    /**
     * Load all drafts by the influencer in the campaign
     * @throws {Error}
     */
    async loadDeliverableDrafts({ commit, state }) {
        if (!state.campaign) {
            throw new Error('campaign/loadDeliverableDrafts: campaign not loaded.');
        }
        const url = `/drafts/${state.campaign.handle}`;
        const { data } = await axios.get(url);
        // Order drafts: render drafts with submissions first, then follow chronological order
        const orderedDrafts = data.data
            .sort((a, unusedB) => (a.submissions.length > 0 ? -1 : 1))
            .sort((a, b) => ((Date.parse(a.createdAt) < Date.parse(b.createdAt)) ? -1 : 1));
        commit('setDeliverableDrafts', orderedDrafts.map((draft) => new DeliverableDraft(draft)));
    },

    /**
     * Load the tiktok orders for the campaign
     * @throws {Error}
     */
    async loadTiktokOrders({ commit, state }) {
        if (!state.campaign) {
            throw new Error('campaign/loadTiktokOrders: campaign not loaded.');
        }
        const url = `/campaigns/${state.campaign.handle}/tiktokOrders`;
        const { data } = await axios.get(url);
        commit('setTiktokOrders', data);
    },

    async loadTtoCampaign({ commit, state }) {
        if (!state.campaign) {
            throw new Error('campaign/loadTtoCampaign: campaign not loaded.');
        }
        const url = `/campaigns/${state.campaign.handle}/ttoCampaign`;
        const { data } = await axios.get(url);
        commit('setTtoCampaign', data);
    },
    /**
     * Makes the request to create a Draft Submission with formData
     * If no draft with draftId exists, first create a new Draft
     * @param {Object} param0
     * @param {Object} param1
     * @param {Number} param1.deliverableId
     * @param {String} param1.draftId
     * @param {DraftFormData} param1.formData
     */
    async postDraftSubmission({ state }, { deliverableId, draftId, formData }) {
        if (!state.campaign) {
            throw new Error('campaign/postDraftSubmission: campaign not loaded.');
        }
        if (!state.infUid) {
            throw new Error('campaign/postDraftSubmission: infUid not loaded.');
        }
        const { campaign, infUid, deliverables, deliverableDrafts } = state;

        const draft = draftId ? deliverableDrafts.find((draft) => draft.id === draftId) : null;
        const deliverable = deliverableId ? deliverables.find((deliverable) => deliverable.id === deliverableId) : null;

        const encodedFormData = new FormData();
        // The file must come at the end
        const sortedFormDataEntries = Object.entries(formData).sort(([key, unusedValue], unusedB) => (key === 'file' ? 1 : -1));
        for (const [key, value] of sortedFormDataEntries) {
            encodedFormData.append(key, value);
        }

        if (draft) {
            await axios.put(
                `/drafts/${campaign.handle}/${draft.id}/submissions`,
                encodedFormData
            );
        } else if (deliverable) {
            const { data } = await axios.put(
                `/drafts/${campaign.handle}`,
                {
                    infUid,
                    projectId: campaign.projectId,
                    deliverableId: deliverable.id
                }
            );

            await axios.put(
                `/drafts/${campaign.handle}/${data.data.id}/submissions`,
                encodedFormData
            );
        } else {
            throw new Error('campaign/saveDraftSubmission: draft or deliverable are required');
        }
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
